import styled from '@emotion/styled';
import { useTranslate } from '@tolgee/react';
import { useCreation, useMemoizedFn, useRequest } from 'ahooks';
import { Options as RequestionOptions } from 'ahooks/lib/useRequest/src/types';
import { Cascader, CascaderProps, SelectProps, Tag, Tooltip } from 'antd';
import { newPermissionState } from 'hooks/business/useNewPermisson';
import { isNil } from 'lodash-es';
import { memo } from 'react';
import { useAtomValue } from 'jotai';
import { listDepartmentAndRoles } from 'services/api/department/department';
import { excludeRoleList, translateDepartment, translateRole } from 'views/Assistant/utils';

const LabelWrapper = styled.span`
  > span {
    margin: 0 0 0 8px;
  }
`;

interface Department extends StoreHomeV2.Department {}
interface RoleWithDepartment extends StoreHomeV2.RoleWithDepartment {}
/**
 * 级联选择器类型
 */
interface RoleCascaderType {
  roleId: RoleWithDepartment['id'];
  departmentId: Department['id'];
}
export interface RoleCascaderProps
  extends Omit<SelectProps, 'onChange' | 'options' | 'children' | 'value'> {
  /**
   * @description roleId 全局自增，跨部门不冲突
   * @description 因此可以使用 role id作为value
   */
  value?: RoleWithDepartment['id'];
  /**
   * @description 数据源
   */
  dataSource: StoreHomeV2.DepartmentWithRoles[];
  /**
   * 禁用字段
   */
  disabledFields?: Partial<RoleCascaderType>[];
  onChange?: (value: RoleCascaderType) => void;
  staleTime?: number;
  permissionJudge?: boolean;
  accountType?: Assistant.AccountType;
}

export const useRoleCascader = (
  options?: RequestionOptions<StoreHomeV2.DepartmentWithRoles[], any[]>,
) => {
  const request = useRequest<StoreHomeV2.DepartmentWithRoles[], any[]>(
    () => listDepartmentAndRoles().then((r) => r.departmentAndRoleOfItList),
    {
      cacheKey: '@@RoleCascader_listDepartmentAndRoles',
      ...options,
    },
  );
  return { request };
};

export const isAssignConversationRole = ({
  isAdmin,
  assignConversation,
}: StoreHomeV2.RoleWithDepartment) => {
  return !isAdmin && assignConversation;
};

const RoleCascader = (props: RoleCascaderProps) => {
  const {
    value,
    disabledFields = [],
    onChange: onChangeProp,
    dataSource: list,
    permissionJudge = false,
    accountType,
    ...otherProps
  } = props;
  const { t } = useTranslate();

  const { roles = [] } = useAtomValue(newPermissionState);

  type Options = Exclude<CascaderProps<any>['options'], undefined>;
  const options = useCreation<Options>(() => {
    if (!list) {
      return [];
    }

    const hasDepartmentPermissionRoleIdMap = roles.reduce<Record<number, true>>((res, role) => {
      const { id, permissionWithScopes = [] } = role;

      const hasDepartmentPermission = permissionWithScopes.some(
        ({ permissionName }) => permissionName === '帐号管理',
      );

      if (hasDepartmentPermission) {
        res[id] = true;
      }

      return res;
    }, {});

    const getRoleTypeTag = (role: StoreHomeV2.RoleWithDepartment) => {
      const { isAdmin, assignConversation } = role;

      if (isAdmin) {
        return <Tag color="green">{t('department.监督全部')}</Tag>;
      }

      if (assignConversation) {
        return <Tag color="processing">{t('department.智能分配')}</Tag>;
      }

      return <Tag color="default">{t('department.不予分配')}</Tag>;
    };

    return list.reduce((res, dept) => {
      const hasDepartmentPermission =
        roles.some((_) => excludeRoleList.includes(_.name as any)) ||
        dept.roles.some(({ id }) => hasDepartmentPermissionRoleIdMap[id]);

      const option: Options[number] = {
        raw: dept,
        value: dept.id,
        label: translateDepartment(dept, t),
        disabled:
          disabledFields.some((i) => i.departmentId === dept.id) ||
          (permissionJudge && !hasDepartmentPermission),
        children:
          dept?.roles?.map?.((role) => {
            const disabled = accountType === 2 && isAssignConversationRole(role);

            const label = (
              <LabelWrapper>
                {translateRole(role.name, t)}
                {getRoleTypeTag(role)}
              </LabelWrapper>
            );

            return {
              raw: role,
              value: role.id,
              label: disabled ? (
                <Tooltip title={t('account.限额帐号不支持选择智能分配模式')}>{label}</Tooltip>
              ) : (
                label
              ),
              disabled: disabled || disabledFields.some((i) => i.roleId === role.id),
            };
          }) || [],
      };
      res.push(option);
      return res;
    }, [] as Options);
  }, [list, disabledFields, roles, t]);
  const handleChange = useMemoizedFn((value?: any[]) => {
    const [departmentId, roleId] = value || [];
    onChangeProp?.({ roleId, departmentId });
  });
  const finalValue = useCreation(() => {
    if (isNil(value)) {
      return [];
    }
    const dept = list?.find?.((dept) => dept.roles?.find?.((r) => r.id === value)) || null;
    if (!dept) {
      return [];
    }
    return [dept.id, value];
  }, [list, value]);
  return (
    <Cascader
      placeholder={t('department.选择x', { x: t('department.角色') })}
      value={finalValue}
      onChange={handleChange}
      options={options}
      {...otherProps}
    />
  );
};
export default memo(RoleCascader);
